/**
 * Pure-function policy for when an incoming call may override the browser's
 * "do not disturb" silencing.
 *
 * Browser equivalent of iOS `CallKitDNDPolicy.swift`. There is no web API
 * that directly mirrors `CXProviderConfiguration`'s DND bypass, but the
 * policy logic is identical — the consumer wires the decision to whichever
 * notification / audio surface it controls.
 *
 * Design intent (mirrors iOS):
 *
 *   - The user's notification silencing preference is the authoritative wish.
 *     Overriding it should be a deliberate opt-in per peer, not an emergent
 *     property of "they are calling me right now."
 *
 *   - Apple's standard "repeated call within 3 minutes" pattern is the one
 *     exception we honour: a second call from the same peer within 180 s
 *     matches the system-wide convention for escalating real emergencies, and
 *     most users expect it to work. We mirror this exactly.
 *
 *   - There is no "VIP contact" auto-override — that is exactly the abuse
 *     vector this module is trying to close.
 *
 * All decisions are pure functions of (PeerCallSettings, elapsed seconds
 * since previous call). No browser API is imported.
 */

import type { PeerCallSettings } from "./CallDisplayPolicy";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Seconds within which a repeated call from the same peer may override DND. */
export const DND_REPEAT_WINDOW_SECONDS = 180;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The DND-override decision with an explanatory reason string.
 *
 * Web mirror of iOS `CallKitDNDDecision`.
 */
export interface CallDNDDecision {
  readonly shouldBypassDND: boolean;
  /** Human-readable reason for the decision. For diagnostics only. */
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Decide whether this incoming call may override DND silencing.
 *
 * `secondsSinceLastCall` is the elapsed time since the previous call from
 * this peer (from the caller's own tracking state), or `undefined` if there
 * was no previous call or the tracker has no record. The 180 s threshold
 * mirrors Apple's documented "repeated call" Focus exception.
 *
 * Web mirror of iOS `CallKitDNDPolicy.decide`.
 *
 * | allowDNDOverride | secondsSinceLastCall | Decision                               |
 * |------------------|----------------------|----------------------------------------|
 * | true             | any                  | bypass — "explicit peer-override permission" |
 * | false            | undefined            | respect — "default DND respected"      |
 * | false            | ≤ 180                | bypass — "repeat call within 3 min"    |
 * | false            | > 180                | respect — "default DND respected"      |
 */
export function decideCallDND(
  callerSettings: Pick<PeerCallSettings, "allowDNDOverride">,
  secondsSinceLastCall: number | undefined,
): CallDNDDecision {
  if (callerSettings.allowDNDOverride) {
    return {
      shouldBypassDND: true,
      reason: "explicit peer-override permission",
    };
  }

  if (
    secondsSinceLastCall !== undefined &&
    secondsSinceLastCall <= DND_REPEAT_WINDOW_SECONDS
  ) {
    return {
      shouldBypassDND: true,
      reason: "repeat call within 3 min",
    };
  }

  return {
    shouldBypassDND: false,
    reason: "default DND respected",
  };
}
